import { useCallback, useEffect, useRef, useState } from 'react'
import {
  CalendarDays,
  Newspaper,
  ListChecks,
  MessageSquare,
  Images,
  UtensilsCrossed,
  PiggyBank,
  Cake,
  ClipboardCheck,
  CalendarClock,
  LogOut,
  Loader2,
  Settings,
  LayoutGrid,
  X,
} from 'lucide-react'
import type { MemberDto } from '@familynest/shared'
import { useAuth } from '../store/auth'
import { api } from '../lib/api'
import { connectWs, useLive } from '../lib/ws'
import { initNativePush } from '../lib/push'
import { ProfileDialog } from './ProfileDialog'
import { Wordmark } from './Logo'
import { Avatar } from './ui'
import { FeedScreen } from './feed/FeedScreen'
import { CalendarScreen } from './calendar/CalendarScreen'
import { ListsScreen } from './lists/ListsScreen'
import { ChatScreen } from './chat/ChatScreen'
import { AllowanceScreen } from './allowance/AllowanceScreen'
import { GalleryScreen } from './gallery/GalleryScreen'
import { MealsScreen } from './meals/MealsScreen'
import { OccasionsTab } from './birthdays/OccasionsTab'
import { SubscriptionsScreen } from './calendar/SubscriptionsScreen'
import { ShiftPlanScreen } from './shift/ShiftPlanScreen'
import { TasksScreen } from './tasks/TasksScreen'
import { MembersScreen } from './MembersScreen'
import { PushBanner } from './PushBanner'

type ModuleKey = 'feed' | 'calendar' | 'shift' | 'occasions' | 'lists' | 'tasks' | 'chat' | 'gallery' | 'meals' | 'allowance' | 'settings'

// `primary`: erscheint mobil fest in der Bottom-Bar. Alle übrigen Einträge sind
// mobil über den „Mehr"-Button (Bottom-Sheet) erreichbar; Desktop zeigt die
// vollständige Icon-Rail. So bleiben alle ~11 Bereiche auf dem Handy erreichbar.
const NAV: Array<{ key: ModuleKey; label: string; icon: typeof CalendarDays; ready?: boolean; adultOnly?: boolean; primary?: boolean }> = [
  { key: 'feed', label: 'Feed', icon: Newspaper, ready: true, primary: true },
  { key: 'calendar', label: 'Kalender', icon: CalendarDays, ready: true, primary: true },
  { key: 'chat', label: 'Nachrichten', icon: MessageSquare, ready: true, primary: true },
  { key: 'tasks', label: 'Aufgaben', icon: ClipboardCheck, ready: true, primary: true },
  { key: 'lists', label: 'Listen', icon: ListChecks, ready: true, primary: true },
  { key: 'occasions', label: 'Anlässe', icon: Cake, ready: true },
  { key: 'gallery', label: 'Galerie', icon: Images, ready: true },
  { key: 'meals', label: 'Mahlzeiten', icon: UtensilsCrossed, ready: true },
  { key: 'allowance', label: 'Taschengeld', icon: PiggyBank, ready: true },
  { key: 'shift', label: 'Dienstplan', icon: CalendarClock, ready: true, adultOnly: true },
]

export function AppShell(): React.JSX.Element {
  const { member, logout } = useAuth()
  const [active, setActive] = useState<ModuleKey>('feed')
  const [members, setMembers] = useState<MemberDto[] | null>(null)
  const [chatUnread, setChatUnread] = useState(0)
  const [feedUnread, setFeedUnread] = useState(0)
  const [moreOpen, setMoreOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  // Aktueller Tab als Ref (für den stabilen WS-Callback ohne Re-Registrierung).
  const activeRef = useRef(active)
  activeRef.current = active
  // Rollengefilterte Navigation (z. B. Dienstplan nur für Eltern).
  const nav = NAV.filter((n) => !n.adultOnly || member?.role === 'adult')
  const primaryNav = nav.filter((n) => n.primary)
  const secondaryNav = nav.filter((n) => !n.primary)
  // „Mehr" gilt als aktiv, solange ein Sekundär-Bereich geöffnet ist.
  const secondaryActive = secondaryNav.some((n) => n.key === active)

  const loadMembers = (): void => {
    api
      .listMembers()
      .then(setMembers)
      .catch(() => setMembers([]))
  }
  const loadChatUnread = useCallback((): void => {
    if (member?.role === 'guest') return
    api
      .listConversations()
      .then((cs) => setChatUnread(cs.reduce((n, c) => n + c.unreadCount, 0)))
      .catch(() => {})
  }, [member?.role])
  useEffect(() => {
    connectWs()
    loadMembers()
    loadChatUnread()
    // Native (Capacitor): FCM-Token registrieren + Tap-Handler (ADR-042 §3).
    void initNativePush((url) => {
      if (url.includes('chat')) setActive('chat')
    })
  }, [loadChatUnread])
  useLive('chat', loadChatUnread)

  // Feed-Unread-Badge (ADR-045): auf dem Feed-Tab als gesehen markieren, sonst zählen.
  const refreshFeed = useCallback((): void => {
    if (activeRef.current === 'feed') {
      void api.feedMarkSeen().catch(() => {})
      setFeedUnread(0)
    } else {
      api.feedUnread().then(setFeedUnread).catch(() => {})
    }
  }, [])
  useLive('feed', refreshFeed)
  useEffect(() => {
    if (active === 'feed') {
      void api.feedMarkSeen().catch(() => {})
      setFeedUnread(0)
    }
  }, [active])

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <header
        className="flex items-center gap-3 border-b px-3 py-2"
        style={{
          borderColor: 'var(--color-line)',
          backgroundColor: 'var(--color-card)',
          // Native: Header-Höhe um die Statusleisten-Safe-Area erweitern (py-2 = 0.5rem).
          paddingTop: 'calc(0.5rem + env(safe-area-inset-top))',
        }}
      >
        <img src="/icon.png" width={40} height={40} alt="" />
        <Wordmark className="text-xl" />
        <div className="ml-auto flex items-center gap-3">
          {member && (
            <button
              onClick={() => setProfileOpen(true)}
              className="flex items-center gap-2 rounded-lg p-1 hover:bg-black/5"
              title="Profilbild ändern"
              aria-label="Profilbild ändern"
            >
              <Avatar name={member.name} color={member.color} size={28} src={member.avatarUrl} />
              <span className="hidden text-sm sm:inline" style={{ color: 'var(--color-ink)' }}>
                {member.name}
              </span>
            </button>
          )}
          {member?.role === 'adult' && (
            <button
              onClick={() => setActive('settings')}
              aria-label="Einstellungen"
              title="Einstellungen"
              className="rounded-lg p-2 hover:bg-black/5"
              style={active === 'settings' ? { color: 'var(--color-accent)' } : { color: 'var(--color-muted)' }}
            >
              <Settings className="size-5" />
            </button>
          )}
          <button
            onClick={() => void logout()}
            aria-label="Abmelden"
            className="rounded-lg p-2 hover:bg-black/5"
            title="Abmelden"
          >
            <LogOut className="size-5" style={{ color: 'var(--color-muted)' }} />
          </button>
        </div>
      </header>

      {member && <PushBanner role={member.role} />}

      <div className="flex min-h-0 flex-1">
        {/* Icon-Rail (Desktop) */}
        <nav
          className="hidden w-24 flex-col items-center gap-1 border-r px-1 py-3 md:flex"
          style={{ borderColor: 'var(--color-line)', backgroundColor: 'var(--color-card)' }}
        >
          {nav.map((n) => (
            <NavButton key={n.key} n={n} active={active === n.key} onClick={() => n.ready && setActive(n.key)} badge={n.key === 'chat' ? chatUnread : n.key === 'feed' ? feedUnread : 0} />
          ))}
        </nav>

        {/* Inhalt */}
        <main className="min-h-0 flex-1 overflow-auto">
          {!members ? (
            <div className="flex h-full items-center justify-center">
              <Loader2 className="size-6 animate-spin" style={{ color: 'var(--color-muted)' }} />
            </div>
          ) : active === 'feed' ? (
            <FeedScreen members={members} />
          ) : active === 'calendar' ? (
            <CalendarScreen members={members} />
          ) : active === 'shift' ? (
            <ShiftPlanScreen />
          ) : active === 'lists' ? (
            <ListsScreen members={members} />
          ) : active === 'tasks' ? (
            <TasksScreen members={members} />
          ) : active === 'chat' ? (
            <ChatScreen members={members} onRead={loadChatUnread} />
          ) : active === 'allowance' ? (
            <AllowanceScreen members={members} />
          ) : active === 'gallery' ? (
            <GalleryScreen />
          ) : active === 'meals' ? (
            <MealsScreen />
          ) : active === 'occasions' ? (
            <OccasionsTab />
          ) : active === 'settings' ? (
            <div className="h-full overflow-auto">
              <SubscriptionsScreen />
              <MembersScreen onChanged={loadMembers} />
            </div>
          ) : (
            <ComingSoon label={NAV.find((n) => n.key === active)?.label ?? ''} />
          )}
        </main>
      </div>

      {/* Bottom-Tabs (Mobil): Primär-Bereiche + „Mehr"-Button */}
      <nav
        className="flex items-center justify-around border-t py-1 md:hidden"
        style={{
          borderColor: 'var(--color-line)',
          backgroundColor: 'var(--color-card)',
          // Native: unter der Android-Gestenleiste Platz lassen (py-1 = 0.25rem).
          paddingBottom: 'calc(0.25rem + env(safe-area-inset-bottom))',
        }}
      >
        {primaryNav.map((n) => (
          <NavButton key={n.key} n={n} active={active === n.key} onClick={() => n.ready && setActive(n.key)} compact badge={n.key === 'chat' ? chatUnread : n.key === 'feed' ? feedUnread : 0} />
        ))}
        <NavButton
          n={{ key: 'more', label: 'Mehr', icon: LayoutGrid, ready: true }}
          active={secondaryActive || moreOpen}
          onClick={() => setMoreOpen(true)}
          compact
        />
      </nav>

      {/* „Mehr"-Sheet (Mobil): alle Sekundär-Bereiche */}
      {moreOpen && (
        <div className="fixed inset-0 z-50 flex items-end bg-black/40 md:hidden" onClick={() => setMoreOpen(false)}>
          <div
            className="w-full rounded-t-2xl border-t p-4"
            style={{
              backgroundColor: 'var(--color-card)',
              borderColor: 'var(--color-line)',
              paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-base font-semibold" style={{ color: 'var(--color-ink)' }}>Mehr</h2>
              <button onClick={() => setMoreOpen(false)} aria-label="Schließen" className="rounded-lg p-1 hover:bg-black/5">
                <X className="size-5" style={{ color: 'var(--color-muted)' }} />
              </button>
            </div>
            <div className="grid grid-cols-4 gap-1">
              {secondaryNav.map((n) => (
                <NavButton
                  key={n.key}
                  n={n}
                  active={active === n.key}
                  onClick={() => {
                    if (n.ready) {
                      setActive(n.key)
                      setMoreOpen(false)
                    }
                  }}
                  compact
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {profileOpen && <ProfileDialog onClose={() => setProfileOpen(false)} />}
    </div>
  )
}

function NavButton({
  n,
  active,
  onClick,
  compact = false,
  badge = 0,
}: {
  n: { key: string; label: string; icon: typeof CalendarDays; ready?: boolean }
  active: boolean
  onClick: () => void
  compact?: boolean
  badge?: number
}): React.JSX.Element {
  const Icon = n.icon
  return (
    <button
      onClick={onClick}
      disabled={!n.ready}
      title={n.ready ? n.label : `${n.label} — bald`}
      className={`relative flex flex-col items-center gap-0.5 rounded-lg px-1 py-1.5 text-[10px] ${compact ? 'flex-1' : 'w-full'} disabled:opacity-40`}
      style={active ? { backgroundColor: 'var(--color-accent)', color: '#fff' } : { color: 'var(--color-muted)' }}
    >
      <span className="relative">
        <Icon className="size-5" />
        {badge > 0 && (
          <span className="absolute -right-2 -top-1.5 flex min-w-4 items-center justify-center rounded-full px-1 text-[9px] font-semibold text-white" style={{ backgroundColor: 'var(--color-danger)' }}>
            {badge > 9 ? '9+' : badge}
          </span>
        )}
      </span>
      <span className="truncate">{n.label}</span>
    </button>
  )
}

function ComingSoon({ label }: { label: string }): React.JSX.Element {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center">
      <img src="/icon.png" width={64} height={64} alt="" className="opacity-60" />
      <p className="text-lg font-medium" style={{ color: 'var(--color-ink)' }}>
        {label} kommt bald
      </p>
      <p className="text-sm" style={{ color: 'var(--color-muted)' }}>
        Dieser Bereich wird in einem der nächsten Bau-Schritte umgesetzt.
      </p>
    </div>
  )
}
